/**
 * Point-filter / temporary-tracking interactive session (ТЗ §8.6 object
 * snap / point construction + §10.4 drafting aids).
 *
 * AutoCAD point filters and temporary tracking are a TRANSPARENT layer over
 * any point prompt: the user types a filter starter (`FROM`, `M2P`/`MTP`,
 * `.X`/`.Y`/`.Z`) mid-command, supplies one or more auxiliary picks, and the
 * command receives the single COMPOSED point. This module implements that
 * layer for the draft + modify state machines without introducing new
 * `DraftStep`s — the flow state lives entirely in `session.options`, so the
 * underlying command step (e.g. `line-next-point`, `move-destination`) is
 * unchanged and its rubber-band preview keeps rendering.
 *
 * ## Flows
 *
 *   FROM      base (pick/type) → offset (`@dx,dy` / polar / absolute)
 *                              → base+offset
 *   M2P/MTP   first (pick/type) → second (pick/type) → midpoint
 *   .X/.Y/.Z  supply (pick/type, ordinates filtered) → rest (remaining
 *   .XY/…       ordinates) → combined point
 *
 * Token recognition + arithmetic are the single source of truth in
 * `@dxf-ai-editor/coordinate-input` (`pointFilterTokens.ts`); this module
 * only sequences the picks and stores the accumulator.
 *
 * Dependency direction: this module imports the draft TYPES + shared
 * helpers only — never the state machines that import IT — keeping the
 * `draftStateMachine/draftModifyStateMachine → pointFilterSession` edge
 * one-way (monorepo zero-cycle depcruise gate).
 */

import {
  classifyPointFilterToken,
  combineOrdinateFilter,
  midBetween,
  parsePointToken,
  type OrdinateFilterAxes,
} from "@dxf-ai-editor/coordinate-input";

import type { AdvanceResult, DraftPoint, DraftSession } from "./draftTypes";
import {
  draftStepConsumesFreeTextName,
  orthoAnchorForDraftSession,
  referencePointForDraftTextInput,
} from "./draftSharedHelpers";
// The filter-arming gate below consults `DRAFT_STEP_REGISTRY` directly
// rather than the `draftStepAcceptsPointInput` negative-list helper
// (draftSharedHelpers.ts) — that helper has DOCUMENTED drift from the
// registry for `revcloud-arc-length` / `revcloud-style` /
// `revcloud-object-reverse` (registry: `acceptsPointInput: false`; helper:
// `true` — see `draftStepRegistry.test.ts`'s `KNOWN_DRIFT` allowlist,
// deliberately left out-of-scope for a prior HATCHEDIT-only slice since
// fixing the helper itself would alter REVCLOUD/SCALE DI behaviour project-
// wide and needs its own verification). Reading the registry straight here
// keeps the point-filter arm-site correct WITHOUT touching that broader,
// separately-owned drift. (Codex PR #504 P3 — "Use the registry point-input
// gate for filters".)
import { DRAFT_STEP_REGISTRY } from "./drafting/draftStepRegistry";

// ── session.options keys (string-serialised accumulator) ─────────────
const K_KIND = "pointFilterKind";
const K_STAGE = "pointFilterStage";
const K_ANCHOR = "pointFilterAnchor";
const K_AXES = "pointFilterAxes";

type FilterKind = "from" | "mid" | "ordinate";

/** Result of feeding a pick/text into the point-filter layer. */
export interface PointFilterResolution {
  /** `false` → not a filter interaction; caller runs its normal flow. */
  handled: boolean;
  /** Populated when the flow produced the final composed point. */
  resolvedPoint?: DraftPoint;
  /** The session to use next (state advanced, or cleared once resolved). */
  nextSession?: DraftSession;
}

/** True while a FROM / M2P / `.X.Y.Z` flow is mid-collection. */
export function isPointFilterActive(session: DraftSession): boolean {
  return Boolean(session.options[K_KIND]);
}

/**
 * True once an ACTIVE point filter has captured its FIRST auxiliary
 * sub-point (FROM base, M2P first point, ordinate supply point) — i.e. the
 * filter has a real committed anchor, not merely an armed-but-empty flow.
 * Mirrors {@link isDraftAnchored}'s "≥1 committed point" gate in
 * `scene3d/draft/draftAnchor.ts` for the point-filter layer: a captured
 * filter sub-point is just as much a real anchor for viewport in/out-of-frame
 * routing purposes as a committed host-session point is, even while
 * `session.points` itself is still empty (e.g. `LINE` first point -> `M2P` ->
 * first sub-point, all inside the VP, before the host command has ANY point
 * of its own). (Codex PR #504 P2 — "Treat filter anchors as draft-active for
 * VP routing".)
 */
export function pointFilterHasCapturedAnchor(session: DraftSession): boolean {
  return isPointFilterActive(session) && readAnchor(session) !== null;
}

/**
 * Draft-aid (ORTHO / POLAR) anchor accounting for an ACTIVE point filter.
 *
 * While a FROM / M2P / `.X.Y.Z` flow is mid-collection the host session keeps
 * its original `step` + `points`, so the registry-driven
 * {@link orthoAnchorForDraftSession} would lock the next cursor pick onto the
 * HOST command's `$LASTPOINT` (e.g. the LINE's previous endpoint). But once the
 * filter has captured its FIRST auxiliary sub-point, AutoCAD makes that
 * captured point the `$LASTPOINT` the 0/90° (and polar) lock projects onto — the
 * M2P second click, the ordinate `rest` pick, and the FROM direct-distance hover
 * all rubber-band from the filter anchor, not the host reference. Preferring the
 * captured anchor here keeps the ortho-/polar-locked cursor (and therefore the
 * composed point) taken from the correct location.
 *
 * Before the first sub-point is captured (no anchor stored yet — M2P `first`,
 * ordinate `supply`, FROM `base`) and when no filter is active, this falls back
 * to the host registry anchor, so the first sub-pick keeps the host command's
 * ortho behaviour. (Codex PR #504 P2.)
 */
export function orthoAnchorForDraftSessionWithFilter(
  session: DraftSession,
): DraftPoint | null {
  if (isPointFilterActive(session)) {
    const anchor = readAnchor(session);
    if (anchor) return anchor;
  }
  return orthoAnchorForDraftSession(session);
}

/**
 * Dynamic-Input (B8) reference point accounting for an ACTIVE point filter.
 *
 * `App.tsx` feeds DI's distance/angle fields the HOST session's last picked
 * point (`session.points.at(-1)`) as the measurement reference. That is
 * correct once a filter has resolved and cleared, but while a FROM / M2P /
 * `.X.Y.Z` flow is mid-collection — after it has captured its first
 * auxiliary sub-point — a typed DI continuation (`@d<angle>`) must measure
 * from THAT captured anchor, the same reference `parseFilterInput` and
 * {@link orthoAnchorForDraftSessionWithFilter} already use, not the host's
 * last point; otherwise DI silently computes a direction the user never
 * typed or saw. Before the first sub-point is captured, or when no filter
 * is active, falls back to the host's own last point (unchanged from the
 * pre-filter behaviour). (Codex PR #504 P2.)
 */
export function draftReferencePointWithFilter(
  session: DraftSession | null,
): DraftPoint | null {
  if (!session) return null;
  if (isPointFilterActive(session)) {
    const anchor = readAnchor(session);
    if (anchor) return anchor;
  }
  return session.points.length > 0
    ? (session.points[session.points.length - 1] ?? null)
    : null;
}

/**
 * The point a pick would resolve to RIGHT NOW, for the point-filter stages
 * where a pick undergoes composition (M2P `second`, ordinate `rest`) before
 * commit — so preview rendering shows the point that will actually be
 * created, not the raw auxiliary hover. Mirrors the arithmetic in
 * {@link consumePoint} exactly, but as a pure peek (never mutates/clears the
 * session).
 *
 * Returns `null` (no override — caller keeps the raw `hoverPoint`) when: no
 * filter is active; no hover yet; still collecting the FIRST sub-point (M2P
 * `first`, ordinate `supply` — the raw hover IS the target, no composition
 * applies yet); or FROM `offset`, where a directly-picked point is already
 * the absolute offset location verbatim (base ignored — AutoCAD parity, see
 * {@link consumePoint}), so the raw hover already matches the eventual
 * commit. (Codex PR #504 P2 — "Preview the composed filter point".)
 */
export function previewPointForActiveFilter(
  session: DraftSession,
): DraftPoint | null {
  if (!isPointFilterActive(session) || !session.hoverPoint) return null;
  const kind = session.options[K_KIND] as FilterKind | undefined;
  const stage = session.options[K_STAGE];
  const hover = session.hoverPoint;
  if (kind === "mid" && stage === "second") {
    const anchor = readAnchor(session);
    if (!anchor) return null;
    const mid = midBetween({ ...anchor, z: 0 }, { ...hover, z: 0 });
    return { x: mid.x, y: mid.y };
  }
  if (kind === "ordinate" && stage === "rest") {
    const anchor = readAnchor(session);
    if (!anchor) return null;
    const combined = combineOrdinateFilter(
      readAxes(session),
      { ...anchor, z: 0 },
      { ...hover, z: 0 },
    );
    return { x: combined.x, y: combined.y };
  }
  return null;
}

/**
 * Cancel an ACTIVE FROM/M2P/`.X.Y.Z` sub-prompt, returning to the host
 * session unchanged (same `step`/`points`) — exported for
 * `useKeyboardShortcuts.ts`'s canvas Escape handler, which must cancel just
 * the filter sub-prompt (AutoCAD parity: Esc during a `FROM`/`M2P` pick
 * returns to the host command's own point prompt), not the whole draft via
 * `setDraftSession(null)`. No-op (returns `session` unchanged) when no
 * filter is active — callers should still guard with
 * {@link isPointFilterActive} to decide whether to intercept Escape at all.
 * (Codex PR #504 P2 — "Route canvas Escape through the active filter".)
 */
export function clearPointFilter(session: DraftSession): DraftSession {
  const options = { ...session.options };
  delete options[K_KIND];
  delete options[K_STAGE];
  delete options[K_ANCHOR];
  delete options[K_AXES];
  return { ...session, options, hoverPoint: null };
}

function readAnchor(session: DraftSession): DraftPoint | null {
  const raw = session.options[K_ANCHOR];
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as DraftPoint).x === "number" &&
      typeof (parsed as DraftPoint).y === "number"
    ) {
      return { x: (parsed as DraftPoint).x, y: (parsed as DraftPoint).y };
    }
  } catch {
    return null;
  }
  return null;
}

function readAxes(session: DraftSession): OrdinateFilterAxes {
  const raw = session.options[K_AXES];
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null) {
        const a = parsed as Partial<OrdinateFilterAxes>;
        return { x: a.x === true, y: a.y === true, z: a.z === true };
      }
    } catch {
      // fall through to default
    }
  }
  return { x: false, y: false, z: false };
}

function withFilterState(
  session: DraftSession,
  kind: FilterKind,
  stage: string,
  extra: { anchor?: DraftPoint; axes?: OrdinateFilterAxes } = {},
): DraftSession {
  const options: Record<string, string> = {
    ...session.options,
    [K_KIND]: kind,
    [K_STAGE]: stage,
  };
  if (extra.anchor) {
    options[K_ANCHOR] = JSON.stringify({
      x: extra.anchor.x,
      y: extra.anchor.y,
    });
  }
  if (extra.axes) {
    options[K_AXES] = JSON.stringify(extra.axes);
  }
  // Preserve the session's CURRENT hover across the stage transition — do
  // NOT null it. A bare direct-distance continuation (`10`, no `@` prefix)
  // typed right after this transition (no intervening mouse move) needs
  // the direction the cursor was ALREADY at when the transition happened;
  // nulling it here left `parseFilterInput`'s `directionPoint` empty and
  // failed the token with MISSING_DIRECTION_POINT even though the cursor
  // never actually left that position. The stale-preview concern this
  // null previously guarded against is now moot: `previewPointForActiveFilter`
  // composes the correct preview point from whatever hover IS present, so a
  // carried-over hover renders the right thing immediately, not a wrong
  // one. (Codex PR #504 P2 — "Preserve hover across filter state changes".)
  return { ...session, options };
}

/**
 * Parse a typed coordinate token to a 2D draft point using the session's
 * live reference (relative / polar / direct-distance resolve against it).
 */
function parseFilterInput(
  session: DraftSession,
  text: string,
): DraftPoint | null {
  // Continuation sub-prompts — M2P's second point and the ordinate `.X`/…
  // `rest` point — resolve relative / polar / direct-distance tokens against
  // the filter's OWN captured anchor (the sub-prompt's last point), NOT the
  // host command reference, which may be absent (e.g. LINE first point, where
  // `@10,0` would otherwise have no reference and leave the filter armed) or a
  // different location (which would compute the midpoint/rest from the wrong
  // spot). The initial sub-prompt (M2P first, ordinate supply — no anchor
  // captured yet) still falls back to the host reference. (Codex PR #504 P2.)
  const reference =
    readAnchor(session) ?? referencePointForDraftTextInput(session);
  const parsed = parsePointToken(text, {
    referencePoint: reference ? { ...reference, z: 0 } : null,
    // Pass the cursor hover whenever it exists so a bare direct-distance token
    // (`10`) measures from the reference toward the cursor even when the host
    // step carries no reference of its own; `parsePointToken` still requires a
    // `referencePoint`, so the anchor/host fallback above governs whether a
    // direct-distance token resolves. Mirrors the FROM-offset direction gate.
    directionPoint: session.hoverPoint ? { ...session.hoverPoint, z: 0 } : null,
  });
  return parsed.ok ? { x: parsed.value.x, y: parsed.value.y } : null;
}

/**
 * Begin a filter flow when `text` is a starter token. Returns the seeded
 * session, or `null` when `text` is not a filter starter.
 */
function beginPointFilter(
  session: DraftSession,
  text: string,
): DraftSession | null {
  const classification = classifyPointFilterToken(text);
  if (!classification) return null;
  if (classification.kind === "from") {
    return withFilterState(session, "from", "base");
  }
  if (classification.kind === "mid") {
    return withFilterState(session, "mid", "first");
  }
  return withFilterState(session, "ordinate", "supply", {
    axes: classification.axes,
  });
}

/**
 * Advance the flow by ONE captured point (from a pick OR a typed token that
 * the caller already resolved to a point). Returns the composed point when
 * the flow completes, or the advanced session while still collecting.
 */
function consumePoint(
  session: DraftSession,
  point: DraftPoint,
): PointFilterResolution {
  const kind = session.options[K_KIND] as FilterKind | undefined;
  const stage = session.options[K_STAGE];

  if (kind === "from") {
    if (stage === "base") {
      return {
        handled: true,
        nextSession: withFilterState(session, "from", "offset", {
          anchor: point,
        }),
      };
    }
    // stage === "offset": a directly-picked point is the absolute offset
    // location (base ignored, matching an absolute token after FROM).
    return { handled: true, resolvedPoint: point, nextSession: clearPointFilter(session) };
  }

  if (kind === "mid") {
    if (stage === "first") {
      return {
        handled: true,
        nextSession: withFilterState(session, "mid", "second", {
          anchor: point,
        }),
      };
    }
    const first = readAnchor(session) ?? point;
    const mid = midBetween(
      { ...first, z: 0 },
      { ...point, z: 0 },
    );
    return {
      handled: true,
      resolvedPoint: { x: mid.x, y: mid.y },
      nextSession: clearPointFilter(session),
    };
  }

  // kind === "ordinate"
  if (stage === "supply") {
    const axes = readAxes(session);
    // A `.XY` filter already fixes BOTH planar ordinates the 2D drafting
    // pipeline uses (`DraftPoint` carries only x/y — the z a follow-up point
    // would contribute is discarded on resolve), so complete immediately
    // instead of advancing to a `rest` stage that would consume the user's
    // next pick/text as a dummy Z continuation before emitting the same point.
    // Single-ordinate (`.X`/`.Y`/`.Z`) and z-including (`.XZ`/`.YZ`) filters
    // still need the remaining planar ordinate, so they advance to `rest`.
    // (Codex PR #504 P2.)
    if (axes.x && axes.y) {
      return {
        handled: true,
        resolvedPoint: { x: point.x, y: point.y },
        nextSession: clearPointFilter(session),
      };
    }
    return {
      handled: true,
      nextSession: withFilterState(session, "ordinate", "rest", {
        anchor: point,
        axes,
      }),
    };
  }
  const supplied = readAnchor(session) ?? point;
  const combined = combineOrdinateFilter(
    readAxes(session),
    { ...supplied, z: 0 },
    { ...point, z: 0 },
  );
  return {
    handled: true,
    resolvedPoint: { x: combined.x, y: combined.y },
    nextSession: clearPointFilter(session),
  };
}

/**
 * Feed typed command-line text into the point-filter layer.
 *
 *  - inactive + starter token → begins the flow (handled, still collecting)
 *  - inactive + ordinary text → not handled (caller parses normally)
 *  - active   + empty (Enter)  → cancels the flow (handled)
 *  - active   + coordinate     → advances / completes the flow
 */
export function advancePointFilterWithText(
  session: DraftSession,
  text: string,
): PointFilterResolution {
  if (!isPointFilterActive(session)) {
    const started = beginPointFilter(session, text);
    if (started) {
      return { handled: true, nextSession: started };
    }
    return { handled: false };
  }

  // An empty Enter mid-flow cancels the tracking (AutoCAD: Esc/empty aborts
  // the point-filter sub-prompt and returns to the host prompt). The
  // Dynamic Input overlay's Escape handler (`DynInputOverlay.handleEscape`
  // -> `onCancel` -> `submitCommandTextRef.current` wired through
  // `useStageContextMenu`'s cancel action) submits the literal `\x1b`
  // control character, not an empty string; `useDraftSession.ts` only
  // special-cases the WORD "ESC" (case-insensitive) before dispatch, so a
  // real Escape keypress reaches here as `\x1b` and must cancel exactly
  // like empty input — otherwise it falls through to the continuation
  // parser, fails as an unparseable token, and leaves the filter armed
  // instead of cancelling. (Codex PR #504 P2 — "Handle Escape when
  // cancelling point filters".)
  if (text.trim() === "" || text === "\x1b" || text.toUpperCase() === "ESC") {
    return { handled: true, nextSession: clearPointFilter(session) };
  }

  // FROM offset must be resolved against the captured base so `@dx,dy`
  // means base+offset. All other stages parse the token against the live
  // reference, then apply the midpoint / ordinate arithmetic via
  // `consumePoint`.
  const kind = session.options[K_KIND] as FilterKind | undefined;
  const stage = session.options[K_STAGE];
  if (kind === "from" && stage === "offset") {
    const base = readAnchor(session);
    const reference = referencePointForDraftTextInput(session);
    const resolved = parsePointToken(text, {
      referencePoint: reference ? { ...reference, z: 0 } : null,
      // The captured FROM base — not the host step's reference — anchors the
      // offset, so a bare direct-distance token (`10`) measures from the base
      // toward the cursor. Pass the hover whenever it exists, even when the
      // host step has no reference (e.g. the first LINE point): gating on
      // `reference` here would drop the direction and mis-parse `10` as
      // MISSING_DIRECTION_POINT, leaving the filter armed. (Codex PR #504 P2.)
      directionPoint: session.hoverPoint
        ? { ...session.hoverPoint, z: 0 }
        : null,
      filter: base ? { fromBase: { ...base, z: 0 } } : null,
    });
    if (!resolved.ok) {
      // Keep collecting — an unparseable offset leaves the flow armed.
      return { handled: true, nextSession: session };
    }
    return {
      handled: true,
      resolvedPoint: { x: resolved.value.x, y: resolved.value.y },
      nextSession: clearPointFilter(session),
    };
  }

  const point = parseFilterInput(session, text);
  if (!point) {
    return { handled: true, nextSession: session };
  }
  return consumePoint(session, point);
}

/**
 * Feed a picked point into the point-filter layer. A pick never STARTS a
 * flow (starters are typed keywords), so an inactive session is not
 * handled — the caller runs its normal pick logic.
 */
export function advancePointFilterWithPoint(
  session: DraftSession,
  point: DraftPoint,
): PointFilterResolution {
  if (!isPointFilterActive(session)) {
    return { handled: false };
  }
  return consumePoint(session, point);
}

/**
 * Interception shim for a text-input dispatcher. Returns an `AdvanceResult`
 * when the point-filter layer handled the input (either still collecting or
 * completed via `redispatchPoint`), or `null` to fall through to the host
 * command's normal text handling.
 *
 * `redispatchPoint` re-enters the host command's point pipeline with the
 * composed point and the cleared session, so the resolved point drives the
 * command exactly as a direct pick would.
 */
export function interceptPointFilterText(
  session: DraftSession,
  text: string,
  redispatchPoint: (session: DraftSession, point: DraftPoint) => AdvanceResult,
): AdvanceResult | null {
  // Start a filter only from a genuine POINT prompt. A step that does not
  // accept point input, OR one whose command-line text is a free-form NAME
  // (block-name / hatch-pattern — point-accepting for pick/preview but where a
  // typed `FROM`/`M2P` is a legal name), must fall through to the host text
  // handler. An already-active filter (only startable at a point prompt) is
  // still driven so its continuation / cancel keep working. (Codex PR #504 P3.)
  if (
    !isPointFilterActive(session) &&
    (!DRAFT_STEP_REGISTRY[session.step].acceptsPointInput ||
      draftStepConsumesFreeTextName(session.step))
  ) {
    return null;
  }
  const result = advancePointFilterWithText(session, text);
  if (!result.handled) {
    return null;
  }
  if (result.resolvedPoint) {
    return redispatchPoint(result.nextSession ?? session, result.resolvedPoint);
  }
  return { nextSession: result.nextSession ?? session };
}

/**
 * Interception shim for a point-input dispatcher. Mirrors
 * {@link interceptPointFilterText} for picked points.
 */
export function interceptPointFilterPoint(
  session: DraftSession,
  point: DraftPoint,
  redispatchPoint: (session: DraftSession, point: DraftPoint) => AdvanceResult,
): AdvanceResult | null {
  const result = advancePointFilterWithPoint(session, point);
  if (!result.handled) {
    return null;
  }
  if (result.resolvedPoint) {
    return redispatchPoint(result.nextSession ?? session, result.resolvedPoint);
  }
  return { nextSession: result.nextSession ?? session };
}
